/**
 * The empty surface. Three variants, because "nothing yet", "nothing matches" and "it broke" ask
 * for different next moves. Compact by design — no illustration, no hero.
 */
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  body?: string;
  action?: ReactNode;
  variant: "first-run" | "filtered" | "error";
  className?: string;
}

const ICON_TONE: Record<EmptyStateProps["variant"], string> = {
  "first-run": "text-muted-foreground",
  filtered: "text-muted-foreground",
  error: "text-state-run-failed-fg",
};

export function EmptyState({
  icon: Icon,
  title,
  body,
  action,
  variant,
  className,
}: EmptyStateProps) {
  return (
    <div
      role={variant === "error" ? "alert" : undefined}
      className={cn(
        "border-border flex flex-col items-center gap-2 rounded-[var(--radius)] border border-dashed px-6 py-8 text-center",
        className,
      )}
    >
      <Icon className={cn("size-5", ICON_TONE[variant])} aria-hidden />
      <p className="type-subhead">{title}</p>
      {body && <p className="type-caption text-muted-foreground max-w-prose">{body}</p>}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}
